// Composition root: wires viewState (camera + fractal params), controls
// (sidebar DOM), and renderLoop (GPU render pipeline) into a working page.
// Pure DOM glue — all WebGPU work happens in fractals/render.mjs.

import { init, gpuName } from "./init.mjs";
import { state, resetView, zoomAt, panBy } from "./ui/viewState.mjs";
import { buildFormulaOptions, buildExtraParamControls, syncPrecisionControl } from "./ui/controls.mjs";
import { configureRenderLoop, setDevice, queueRender, renderNow } from "./ui/renderLoop.mjs";

const canvas = document.getElementById("canvas");
const canvasCtx = canvas.getContext("2d");
const formulaSelect = document.getElementById("formula-select");
const maxIterInput = document.getElementById("max-iter");
const maxIterValue = document.getElementById("max-iter-value");
const extraParamsEl = document.getElementById("extra-params");
const precisionCheckbox = document.getElementById("precision-f64");
const precisionHint = document.getElementById("precision-hint");
const resetViewBtn = document.getElementById("reset-view");
const gpuNameEl = document.getElementById("gpu-name");
const renderTimeEl = document.getElementById("render-time");
const statusEl = document.getElementById("status");

function resizeCanvasToContainer() {
  const rect = canvas.parentElement.getBoundingClientRect();
  canvas.width = Math.max(1, Math.round(rect.width));
  canvas.height = Math.max(1, Math.round(rect.height));
}

function attachCanvasInteraction() {
  canvas.addEventListener("wheel", (e) => {
    e.preventDefault();
    const rect = canvas.getBoundingClientRect();
    const px = e.clientX - rect.left;
    const py = e.clientY - rect.top;
    zoomAt(canvas.width, canvas.height, px, py, e.deltaY > 0 ? 1.15 : 1 / 1.15);
    queueRender();
  }, { passive: false });

  let dragging = false;
  let lastX = 0;
  let lastY = 0;

  canvas.addEventListener("mousedown", (e) => {
    dragging = true;
    lastX = e.clientX;
    lastY = e.clientY;
    canvas.classList.add("dragging");
  });
  window.addEventListener("mouseup", () => {
    dragging = false;
    canvas.classList.remove("dragging");
  });
  window.addEventListener("mousemove", (e) => {
    if (!dragging) return;
    panBy(e.clientX - lastX, e.clientY - lastY);
    lastX = e.clientX;
    lastY = e.clientY;
    queueRender();
  });
}

function attachControlListeners() {
  formulaSelect.addEventListener("change", () => {
    resetView(formulaSelect.value);
    maxIterInput.value = String(state.maxIter);
    maxIterValue.textContent = String(state.maxIter);
    buildExtraParamControls(extraParamsEl, queueRender);
    syncPrecisionControl(precisionCheckbox, precisionHint);
    queueRender();
  });

  precisionCheckbox.addEventListener("change", () => {
    state.precisionF64 = precisionCheckbox.checked;
    queueRender();
  });

  maxIterInput.addEventListener("input", () => {
    state.maxIter = Number(maxIterInput.value);
    maxIterValue.textContent = String(state.maxIter);
    queueRender();
  });

  resetViewBtn.addEventListener("click", () => {
    const extra = state.extra;
    resetView(state.formulaName);
    state.extra = extra; // keep user-tuned extra params, only reset the view window
    maxIterInput.value = String(state.maxIter);
    maxIterValue.textContent = String(state.maxIter);
    queueRender();
  });

  window.addEventListener("resize", () => {
    resizeCanvasToContainer();
    queueRender();
  });
}

async function main() {
  if (!navigator.gpu) {
    statusEl.textContent = "WebGPU is not supported in this browser. Try a recent Chrome, Edge, or Firefox with WebGPU enabled.";
    return;
  }

  buildFormulaOptions(formulaSelect);
  resetView(formulaSelect.value);
  maxIterInput.value = String(state.maxIter);
  maxIterValue.textContent = String(state.maxIter);
  buildExtraParamControls(extraParamsEl, queueRender);
  syncPrecisionControl(precisionCheckbox, precisionHint);
  resizeCanvasToContainer();
  configureRenderLoop({ canvas, canvasCtx, statusEl, renderTimeEl });
  attachCanvasInteraction();
  attachControlListeners();

  try {
    setDevice(await init());
    gpuNameEl.textContent = gpuName();
  } catch (err) {
    statusEl.textContent = err.message;
    console.error(err);
    return;
  }

  await renderNow();
}

main();
